import {
  ATTACHMENT_BY_ID, ATTACHMENT_IDS, CATALOG_CONTEXT, INLINE_BY_ID, INLINE_IDS, THEME_STARTING_DESIGNS,
  type ThemeStartingDesign,
} from './fixtures.ts';
import { cloneSnapshot, type OperationResult, type Snapshot } from './model.ts';
import type { QuizConfig } from './quiz-config.ts';
import { validateQuizConfig, type QuizAnswers } from './quiz-model.ts';

const FORMAL_THEMES = new Set([
  'protection', 'peace-and-blessings', 'abundance', 'calm', 'zodiac', 'meaningful-gifts',
]);

type FormalMapping = {
  questionId: string;
  optionCode: string;
  themes: string[];
  explanation: string;
};

export type FormalRecommendationConfig = {
  mode: 'formal_preview' | 'formal';
  quiz: QuizConfig;
  mappingVersion: string;
  catalogVersion: string;
  mappings: FormalMapping[];
  studioUrl: string;
  validationUrl: string;
  restNonce: string;
  configurationTag: string;
};

export type ResolvedFormalTemplate = ThemeStartingDesign & { theme: string };

export type FormalRecommendationResult =
  | {
    status: 'ready'; quizVersion: string; mappingVersion: string; catalogVersion: string;
    templates: ResolvedFormalTemplate[]; explanations: string[];
  }
  | {
    status: 'unavailable'; quizVersion: string; mappingVersion: string; catalogVersion: string;
    unavailableReasons: string[];
  };

let activeConfig: FormalRecommendationConfig | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unavailable(config: FormalRecommendationConfig, reason: string): FormalRecommendationResult {
  return {
    status: 'unavailable', quizVersion: config.quiz.quizVersion, mappingVersion: config.mappingVersion,
    catalogVersion: config.catalogVersion, unavailableReasons: [reason],
  };
}

function fixtureAvailable(id: string, attachment: boolean): boolean {
  const fixture = attachment ? ATTACHMENT_BY_ID.get(id) : INLINE_BY_ID.get(id);
  return Boolean(fixture && fixture.available === true && (fixture.availableQuantity ?? 0) > 0);
}

function currentTemplate(theme: string): ResolvedFormalTemplate | null {
  const template = THEME_STARTING_DESIGNS.get(theme);
  if (!template || template.inlineComponentIds.length === 0) return null;
  const required = new Map<string, number>();
  template.inlineComponentIds.forEach((id) => required.set(id, (required.get(id) ?? 0) + 1));
  if ([...required].some(([id, quantity]) => {
    const fixture = INLINE_BY_ID.get(id);
    return !INLINE_IDS.has(id) || !fixtureAvailable(id, false) || (fixture?.availableQuantity ?? 0) < quantity;
  })) return null;
  if (template.attachment && (!ATTACHMENT_IDS.has(template.attachment.componentId)
    || !fixtureAvailable(template.attachment.componentId, true)
    || !Number.isInteger(template.attachment.anchorIndex) || template.attachment.anchorIndex < 0
    || template.attachment.anchorIndex >= template.inlineComponentIds.length)) return null;
  return {
    ...template, theme, inlineComponentIds: [...template.inlineComponentIds],
    attachment: template.attachment ? { ...template.attachment } : null,
  };
}

export function configureFormalRecommendation(value: unknown): boolean {
  activeConfig = null;
  if (!isRecord(value) || value.enabled !== true || (value.mode !== 'formal_preview' && value.mode !== 'formal')
    || typeof value.quizVersion !== 'string' || !/^[A-Za-z0-9._-]{1,80}$/.test(value.quizVersion)
    || typeof value.mappingVersion !== 'string' || !/^[A-Za-z0-9._-]{1,80}$/.test(value.mappingVersion)
    || typeof value.catalogVersion !== 'string' || value.catalogVersion !== CATALOG_CONTEXT.version
    || CATALOG_CONTEXT.mode !== 'formal_preview' || !Array.isArray(value.questions) || !Array.isArray(value.mappings)
    || typeof value.studioUrl !== 'string' || value.studioUrl.length < 1
    || typeof value.validationUrl !== 'string' || value.validationUrl.length < 1
    || typeof value.restNonce !== 'string' || typeof value.configurationTag !== 'string') return false;

  const quiz: QuizConfig = {
    quizVersion: value.quizVersion,
    reviewStatus: value.mode,
    questions: value.questions as QuizConfig['questions'],
  };
  try {
    if (!validateQuizConfig(quiz)) return false;
  } catch {
    return false;
  }
  const questions = new Map(quiz.questions.map((question) => [question.id, new Set(question.options.map((option) => option.code))]));
  const mappings: FormalMapping[] = [];
  const seen = new Set<string>();
  for (const raw of value.mappings) {
    if (!isRecord(raw) || typeof raw.questionId !== 'string' || typeof raw.optionCode !== 'string'
      || !Array.isArray(raw.themes) || raw.themes.length > 2 || typeof raw.explanation !== 'string'
      || raw.explanation.length > 240 || !questions.get(raw.questionId)?.has(raw.optionCode)) return false;
    const key = `${raw.questionId}:${raw.optionCode}`;
    const themes = raw.themes.filter((theme): theme is string => typeof theme === 'string');
    if (seen.has(key) || themes.length !== raw.themes.length || new Set(themes).size !== themes.length
      || !themes.every((theme) => FORMAL_THEMES.has(theme))) return false;
    seen.add(key);
    mappings.push({ questionId: raw.questionId, optionCode: raw.optionCode, themes, explanation: raw.explanation });
  }
  if (!mappings.some((mapping) => mapping.themes.length > 0)) return false;

  activeConfig = {
    mode: value.mode,
    quiz,
    mappingVersion: value.mappingVersion,
    catalogVersion: value.catalogVersion,
    mappings,
    studioUrl: value.studioUrl,
    validationUrl: value.validationUrl,
    restNonce: value.restNonce,
    configurationTag: value.configurationTag,
  };
  return true;
}

export function getFormalRecommendationConfig(): FormalRecommendationConfig | null {
  return activeConfig;
}

export function clearFormalRecommendation(): void {
  activeConfig = null;
}

export function buildFormalRecommendation(
  answers: QuizAnswers,
  config: FormalRecommendationConfig,
): FormalRecommendationResult {
  if (CATALOG_CONTEXT.mode !== 'formal_preview' || CATALOG_CONTEXT.version !== config.catalogVersion
    || !validateQuizConfig(config.quiz)) {
    return unavailable(config, 'The formal recommendation version no longer matches the active catalog. Continue directly to DIY.');
  }

  const scores = new Map<string, { score: number; order: number }>();
  const explanations: string[] = [];
  let order = 0;
  for (const mapping of config.mappings) {
    if (!(answers[mapping.questionId] ?? []).includes(mapping.optionCode)) continue;
    mapping.themes.forEach((theme, index) => {
      const current = scores.get(theme);
      scores.set(theme, { score: (current?.score ?? 0) + (index === 0 ? 2 : 1), order: current?.order ?? order++ });
    });
    if (mapping.explanation && !explanations.includes(mapping.explanation)) explanations.push(mapping.explanation);
  }
  const selectedThemes = [...scores.entries()]
    .sort((left, right) => right[1].score - left[1].score || left[1].order - right[1].order || left[0].localeCompare(right[0]))
    .slice(0, 2).map(([theme]) => theme);
  if (selectedThemes.length === 0) {
    return unavailable(config, 'No formal starting design is mapped to the choices you kept. Your answers were not replaced with a guess.');
  }
  const templates: ResolvedFormalTemplate[] = [];
  for (const theme of selectedThemes) {
    const template = currentTemplate(theme);
    if (!template) {
      return unavailable(config, `The mapped ${theme.replaceAll('-', ' ')} starting design is currently unavailable. No component was substituted.`);
    }
    templates.push(template);
  }
  return {
    status: 'ready', quizVersion: config.quiz.quizVersion, mappingVersion: config.mappingVersion,
    catalogVersion: config.catalogVersion, templates, explanations,
  };
}

export function applyFormalTemplate(
  snapshot: Snapshot,
  template: ResolvedFormalTemplate,
  config: FormalRecommendationConfig,
): OperationResult {
  if (CATALOG_CONTEXT.mode !== 'formal_preview' || CATALOG_CONTEXT.version !== config.catalogVersion) {
    return { snapshot, error: 'The formal catalog changed. This starting design was not applied.' };
  }
  const current = currentTemplate(template.theme);
  if (!current || current.id !== template.id
    || current.inlineComponentIds.join('\u0000') !== template.inlineComponentIds.join('\u0000')
    || current.attachment?.componentId !== template.attachment?.componentId
    || current.attachment?.anchorIndex !== template.attachment?.anchorIndex) {
    return { snapshot, error: 'This formal starting design is now unavailable. No component was substituted or applied.' };
  }
  const requiredIds = template.inlineComponentIds.length + (template.attachment ? 1 : 0);
  if (!Number.isSafeInteger(snapshot.nextUid) || snapshot.nextUid < 1 || snapshot.nextUid > Number.MAX_SAFE_INTEGER - requiredIds) {
    return { snapshot, error: 'New design instance IDs could not be allocated safely.' };
  }
  const next = cloneSnapshot(snapshot);
  let sequence = snapshot.nextUid;
  next.design.inline_items = template.inlineComponentIds.map((componentId) => ({
    uid: `bead-${(sequence++).toString(36)}`, component_id: componentId, orientation_deg: 0,
  }));
  next.design.attachments = template.attachment ? [{
    uid: `charm-${(sequence++).toString(36)}`,
    component_id: template.attachment.componentId,
    anchor_uid: next.design.inline_items[template.attachment.anchorIndex].uid,
    orientation_deg: 0,
  }] : [];
  next.selectedInlineUid = next.design.inline_items[0]?.uid ?? null;
  next.nextUid = sequence;
  return { snapshot: next };
}

export async function validateFormalSelection(
  config: FormalRecommendationConfig,
  themes: string[],
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (themes.length < 1 || themes.length > 2 || !themes.every((theme) => FORMAL_THEMES.has(theme))) {
    return { ok: false, message: 'The selected formal starting design is invalid.' };
  }
  try {
    const response = await fetch(config.validationUrl, {
      method: 'POST', credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(config.restNonce ? { 'X-WP-Nonce': config.restNonce } : {}),
      },
      body: JSON.stringify({ mapping_version: config.mappingVersion, themes }),
    });
    const payload = await response.json().catch(() => null) as { message?: unknown; valid?: unknown; mapping_version?: unknown; catalog_version?: unknown } | null;
    if (!response.ok || payload?.valid !== true || payload.mapping_version !== config.mappingVersion
      || payload.catalog_version !== config.catalogVersion) {
      return { ok: false, message: typeof payload?.message === 'string' ? payload.message : 'The formal starting design could not be revalidated. Nothing was changed.' };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: 'The formal starting design could not be revalidated. Nothing was changed.' };
  }
}
